import type { FC } from "react";
import { memo, useEffect } from "react";

import useUserStore from "@store/useUserStore";
import { useEditor } from "./hooks/useEditor";
import { cn } from "@utils/tailwind.util";
import { AINote, AINoteAccess } from "@app-types/notes.type";
import { DEFAULT_ACTIONS } from "../../NoteEditor/Toolbar/constants";
import { Toolbar } from "../../NoteEditor/Toolbar/Toolbar";
import { NoteEditorSkeleton } from "./NoteEditorSkeleton";
import { Editor } from "../../NoteEditor/Editor";

interface Props {
  note?: AINote;
  onUpdateNote: (data: Partial<AINote>) => void;
}

const NoteEditor: FC<Props> = memo(({ note, onUpdateNote }) => {
  const { activeUser } = useUserStore();
  const { editor, isSynced } = useEditor();

  const isSharedNotesEnabled = activeUser?.organization?.shared_meeting_notes ?? false;
  const isPrivateNote = note?.access === ("Private" as AINoteAccess);

  const handleClick = () => {
    if (editor && !editor.isFocused) {
      editor.commands.focus("end");
    }
  };

  useEffect(() => {
    // if shared notes are enabled keep notes always public
    if (isSharedNotesEnabled && isPrivateNote) {
      onUpdateNote({ access: "Public" });
    }
  }, [isSharedNotesEnabled, isPrivateNote]);

  if (!editor) return null;

  return (
    <div className="relative flex h-full w-full flex-col justify-between bg-white dark:bg-gray-700 border border-stroke-primary dark:border-gray-600 rounded-xl">
      <div className="max-h-[300px] my-3 overflow-auto" onClick={handleClick}>
        {!isSynced ? (
          <NoteEditorSkeleton className="px-4" />
        ) : (
          <Editor
            editor={editor}
            selectedTags={[]}
            keyForTypes="NOTES"
            className={cn("min-h-[100px] w-full px-4", isSynced && "animate-fade-in")}
          />
        )}
      </div>

      <div className="w-full flex items-center justify-between border-t border-stroke-primary dark:border-gray-600 px-1">
        <Toolbar className="p-1" actions={DEFAULT_ACTIONS} editor={editor} />
      </div>
    </div>
  );
});

export { NoteEditor };
